import { goRequest, formGoClient, goClient } from '../utils/httpClientTs';
import type {
  StatusItemResponse,
  StatusPurchaseOrderResponse,
  CategoryResponse,
  GroupTypeResponse,
  ItemResponse,
  ItemDetailResponse,
  ItemListResponse,
  CreateItemRequest,
  UpdateItemRequest,
  ItemVendorsResponse,
  VendorResponse,
  VendorDetailResponse,
  VendorListResponse,
  CreateVendorRequest,
  UpdateVendorRequest,
  VendorItemResponse,
  CreateVendorItemRequest,
  UpdateVendorItemRequest,
  PurchaseOrderResponse,
  PurchaseOrderDetailResponse,
  PurchaseOrderListResponse,
  CreatePurchaseOrderRequest,
  UpdatePurchaseOrderRequest,
  ReceivePurchaseOrderRequest,
  InventoryResponse,
  InventoryListResponse,
  AdjustInventoryRequest,
  InventoryTransactionTypeResponse,
  KitAssemblyRequest,
  InventoryTransactionListResponse,
  OnlineItemResponse,
  OnlineItemDetailResponse,
  OnlineItemListResponse,
  StorefrontItemsResponse,
  StorefrontCheckoutRequest,
  StorefrontCheckoutResponse,
  StorefrontConfirmPaymentRequest,
  StorefrontConfirmPaymentResponse,
  StorefrontOrdersResponse,
  StorefrontOrderDetailResponse,
  ManagementOrdersResponse,
  ManagementOrderDetailResponse,
  ManagementMailInCheckOrderRequest,
  ManagementMailInCardCheckoutRequest,
  ManagementCheckOrderResponse,
  ManagementOrderApprovalActionResponse,
  ManagementOrderRefundRequest,
  ManagementOrderRefundResponse,
  OrderPaymentMemoDetail,
  ResolveOrderPaymentMemoRequest,
  ManagementUpdateOrderRequest,
  ManagementUpdateOrderLineCustomTextRequest,
  CreateOrderShipmentRequest,
  OrderShipmentResponse,
  UpdateOrderShipmentRequest,
  StatusOrderResponse,
  ListVendorBatchEligibleOrdersResponse,
  GenerateVendorBatchesResponse,
  ListVendorOrderBatchesResponse,
  VendorOrderBatchDetail,
  CreateOnlineItemRequest,
  UpdateOnlineItemRequest,
  AddOnlineItemImageRequest,
  UploadOnlineItemImageResponse,
} from '../types/chapterSupplies';

const BASE = '/api/chapter-supplies';

// --- Lookups ---

export const getItemStatuses = (): Promise<StatusItemResponse[]> =>
  goRequest<StatusItemResponse[]>({
    url: `${BASE}/statuses/items`,
    method: 'GET',
  });

export const getPurchaseOrderStatuses = (): Promise<
  StatusPurchaseOrderResponse[]
> =>
  goRequest<StatusPurchaseOrderResponse[]>({
    url: `${BASE}/statuses/purchase-orders`,
    method: 'GET',
  });

export const getCategories = (): Promise<CategoryResponse[]> =>
  goRequest<CategoryResponse[]>({ url: `${BASE}/categories`, method: 'GET' });

export const getStorefrontCategories = (): Promise<CategoryResponse[]> =>
  goRequest<CategoryResponse[]>({
    url: `${BASE}/storefront/categories`,
    method: 'GET',
  });

export const getGroupTypes = (): Promise<GroupTypeResponse[]> =>
  goRequest<GroupTypeResponse[]>({ url: `${BASE}/group-types`, method: 'GET' });

// --- Items ---

export interface ListItemsParams {
  limit?: number;
  offset?: number;
  /** Search name or SKU (server: `q`) */
  q?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export const listItems = (
  params?: ListItemsParams
): Promise<ItemListResponse> => {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  if (params?.q != null && params.q.trim() !== '')
    search.set('q', params.q.trim());
  if (params?.sort_by != null && params.sort_by.trim() !== '')
    search.set('sort_by', params.sort_by.trim());
  if (params?.sort_order != null) search.set('sort_order', params.sort_order);
  const q = search.toString();
  return goRequest<ItemListResponse>({
    url: `${BASE}/items${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const getItem = (id: string): Promise<ItemDetailResponse> =>
  goRequest<ItemDetailResponse>({ url: `${BASE}/items/${id}`, method: 'GET' });

export const getItemVendors = (itemId: string): Promise<ItemVendorsResponse> =>
  goRequest<ItemVendorsResponse>({
    url: `${BASE}/items/${itemId}/vendors`,
    method: 'GET',
  });

export const createItem = (data: CreateItemRequest): Promise<ItemResponse> =>
  goRequest<ItemResponse>({ url: `${BASE}/items`, method: 'POST', data });

export const updateItem = (
  id: string,
  data: UpdateItemRequest
): Promise<ItemResponse> =>
  goRequest<ItemResponse>({
    url: `${BASE}/items/${id}`,
    method: 'PATCH',
    data,
  });

export const uploadItemImage = (
  file: File
): Promise<UploadOnlineItemImageResponse> => {
  const form = new FormData();
  form.append('image', file);
  return formGoClient
    .post<UploadOnlineItemImageResponse>(`${BASE}/items/upload-image`, form)
    .then((res) => res.data);
};

export const addItemImage = (
  itemId: string,
  data: AddOnlineItemImageRequest
): Promise<{ message: string }> =>
  goRequest<{ message: string }>({
    url: `${BASE}/items/${itemId}/images`,
    method: 'POST',
    data,
  });

export const deleteItemImage = (
  itemId: string,
  imageId: string
): Promise<{ message: string }> =>
  goRequest<{ message: string }>({
    url: `${BASE}/items/${itemId}/images/${imageId}`,
    method: 'DELETE',
  });

// --- Vendors ---

export const listVendors = (params?: {
  limit?: number;
  offset?: number;
}): Promise<VendorListResponse> => {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  const q = search.toString();
  return goRequest<VendorListResponse>({
    url: `${BASE}/vendors${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const getVendor = (id: string): Promise<VendorDetailResponse> =>
  goRequest<VendorDetailResponse>({
    url: `${BASE}/vendors/${id}`,
    method: 'GET',
  });

export const createVendor = (
  data: CreateVendorRequest
): Promise<VendorResponse> =>
  goRequest<VendorResponse>({ url: `${BASE}/vendors`, method: 'POST', data });

export const updateVendor = (
  id: string,
  data: UpdateVendorRequest
): Promise<VendorResponse> =>
  goRequest<VendorResponse>({
    url: `${BASE}/vendors/${id}`,
    method: 'PATCH',
    data,
  });

export const createVendorItem = (
  vendorId: string,
  data: CreateVendorItemRequest
): Promise<VendorItemResponse> =>
  goRequest<VendorItemResponse>({
    url: `${BASE}/vendors/${vendorId}/items`,
    method: 'POST',
    data,
  });

export const updateVendorItem = (
  vendorId: string,
  vendorItemId: string,
  data: UpdateVendorItemRequest
): Promise<VendorItemResponse> =>
  goRequest<VendorItemResponse>({
    url: `${BASE}/vendors/${vendorId}/items/${vendorItemId}`,
    method: 'PATCH',
    data,
  });

export const deleteVendorItem = (
  vendorId: string,
  vendorItemId: string
): Promise<{ message: string }> =>
  goRequest<{ message: string }>({
    url: `${BASE}/vendors/${vendorId}/items/${vendorItemId}`,
    method: 'DELETE',
  });

// --- Purchase orders ---

export const listPurchaseOrders = (params?: {
  limit?: number;
  offset?: number;
}): Promise<PurchaseOrderListResponse> => {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  const q = search.toString();
  return goRequest<PurchaseOrderListResponse>({
    url: `${BASE}/purchase-orders${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const getPurchaseOrder = (
  id: string
): Promise<PurchaseOrderDetailResponse> =>
  goRequest<PurchaseOrderDetailResponse>({
    url: `${BASE}/purchase-orders/${id}`,
    method: 'GET',
  });

export const createPurchaseOrder = (
  data: CreatePurchaseOrderRequest
): Promise<PurchaseOrderResponse> =>
  goRequest<PurchaseOrderResponse>({
    url: `${BASE}/purchase-orders`,
    method: 'POST',
    data,
  });

export const updatePurchaseOrder = (
  id: string,
  data: UpdatePurchaseOrderRequest
): Promise<PurchaseOrderResponse> =>
  goRequest<PurchaseOrderResponse>({
    url: `${BASE}/purchase-orders/${id}`,
    method: 'PATCH',
    data,
  });

export const receivePurchaseOrder = (
  id: string,
  data: ReceivePurchaseOrderRequest
): Promise<PurchaseOrderDetailResponse> =>
  goRequest<PurchaseOrderDetailResponse>({
    url: `${BASE}/purchase-orders/${id}/receive`,
    method: 'POST',
    data,
  });

// --- Storefront customer orders (management) ---

export const listManagementOrders = (params?: {
  limit?: number;
  offset?: number;
  order_status_id?: string;
  /** When true, only orders with at least one internal line still needing shipped quantity */
  internal_fulfillment_open?: boolean;
  /** Order search (min 2 characters); matches purchaser name/email, ship-to name, or order number */
  q?: string;
  /** Substring match on chapter-supplies check number (min 1 character) */
  check_number?: string;
  /** Filter by payment method */
  payment_method?: 'card' | 'check';
  has_open_memo?: boolean;
  memo_type?: 'credit' | 'debit';
  memo_status?: string;
}): Promise<ManagementOrdersResponse> => {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  if (params?.order_status_id != null && params.order_status_id.trim() !== '')
    search.set('order_status_id', params.order_status_id.trim());
  if (params?.internal_fulfillment_open === true)
    search.set('internal_fulfillment_open', '1');
  if (params?.q != null && params.q.trim() !== '')
    search.set('q', params.q.trim());
  if (params?.check_number != null && params.check_number.trim() !== '')
    search.set('check_number', params.check_number.trim());
  if (params?.payment_method != null && params.payment_method.trim() !== '')
    search.set('payment_method', params.payment_method.trim());
  if (params?.has_open_memo === true) search.set('has_open_memo', '1');
  if (params?.memo_type != null && params.memo_type.trim() !== '')
    search.set('memo_type', params.memo_type.trim());
  if (params?.memo_status != null && params.memo_status.trim() !== '')
    search.set('memo_status', params.memo_status.trim());
  const q = search.toString();
  return goRequest<ManagementOrdersResponse>({
    url: `${BASE}/orders${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const createManagementMailInCheckOrder = (
  data: ManagementMailInCheckOrderRequest
): Promise<ManagementCheckOrderResponse> =>
  goRequest<ManagementCheckOrderResponse>({
    url: `${BASE}/orders/mail-in-check`,
    method: 'POST',
    data,
  });

export const checkoutManagementMailInCard = (
  data: ManagementMailInCardCheckoutRequest
): Promise<StorefrontCheckoutResponse> =>
  goRequest<StorefrontCheckoutResponse>({
    url: `${BASE}/orders/mail-in-card/checkout`,
    method: 'POST',
    data,
  });

export const confirmManagementMailInCardPayment = (
  data: StorefrontConfirmPaymentRequest
): Promise<StorefrontConfirmPaymentResponse> =>
  goRequest<StorefrontConfirmPaymentResponse>({
    url: `${BASE}/orders/mail-in-card/confirm`,
    method: 'POST',
    data,
  });

export const getOrderStatuses = (): Promise<StatusOrderResponse[]> =>
  goRequest<StatusOrderResponse[]>({
    url: `${BASE}/statuses/orders`,
    method: 'GET',
  });

export const listVendorBatchEligibleOrders =
  (): Promise<ListVendorBatchEligibleOrdersResponse> =>
    goRequest<ListVendorBatchEligibleOrdersResponse>({
      url: `${BASE}/vendor-batches/eligible-orders`,
      method: 'GET',
    });

export const generateVendorBatches =
  (): Promise<GenerateVendorBatchesResponse> =>
    goRequest<GenerateVendorBatchesResponse>({
      url: `${BASE}/vendor-batches/generate`,
      method: 'POST',
    });

export const listVendorBatches = (params?: {
  limit?: number;
  offset?: number;
}): Promise<ListVendorOrderBatchesResponse> => {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  const q = search.toString();
  return goRequest<ListVendorOrderBatchesResponse>({
    url: `${BASE}/vendor-batches${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const getVendorBatch = (id: string): Promise<VendorOrderBatchDetail> =>
  goRequest<VendorOrderBatchDetail>({
    url: `${BASE}/vendor-batches/${id}`,
    method: 'GET',
  });

export const markVendorBatchSent = (
  id: string
): Promise<VendorOrderBatchDetail> =>
  goRequest<VendorOrderBatchDetail>({
    url: `${BASE}/vendor-batches/${id}/send`,
    method: 'POST',
  });

export const deleteVendorBatch = (id: string): Promise<void> =>
  goRequest({
    url: `${BASE}/vendor-batches/${id}`,
    method: 'DELETE',
  });

export const downloadVendorBatchOrdersPdf = async (
  id: string
): Promise<Blob> => {
  try {
    const response = await goClient.get<Blob>(
      `${BASE}/vendor-batches/${id}/orders-pdf`,
      { responseType: 'blob' }
    );
    return response.data;
  } catch (err: unknown) {
    const ax = err as { response?: { data?: Blob } };
    const data = ax.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const json = JSON.parse(text) as { error?: string };
        throw new Error(json.error || text);
      } catch {
        throw new Error(text || 'PDF download failed');
      }
    }
    throw err;
  }
};

export const downloadVendorBatchLabelsPdf = async (
  id: string
): Promise<Blob> => {
  try {
    const response = await goClient.get<Blob>(
      `${BASE}/vendor-batches/${id}/labels-pdf`,
      { responseType: 'blob' }
    );
    return response.data;
  } catch (err: unknown) {
    const ax = err as { response?: { data?: Blob } };
    const data = ax.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const json = JSON.parse(text) as { error?: string };
        throw new Error(json.error || text);
      } catch {
        throw new Error(text || 'PDF download failed');
      }
    }
    throw err;
  }
};

export const downloadVendorBatchOrdersTxt = async (
  id: string,
  lineType: string
): Promise<Blob> => {
  try {
    const response = await goClient.get<Blob>(
      `${BASE}/vendor-batches/${id}/orders-txt/${lineType}`,
      { responseType: 'blob' }
    );
    return response.data;
  } catch (err: unknown) {
    const ax = err as { response?: { data?: Blob } };
    const data = ax.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const json = JSON.parse(text) as { error?: string };
        throw new Error(json.error || text);
      } catch {
        throw new Error(text || 'TXT download failed');
      }
    }
    throw err;
  }
};

export const downloadPickListsPdf = async (
  orderIds: string[]
): Promise<Blob> => {
  try {
    const response = await goClient.post<Blob>(
      `${BASE}/orders/pick-lists/pdf`,
      { order_ids: orderIds },
      { responseType: 'blob' }
    );
    return response.data;
  } catch (err: unknown) {
    const ax = err as { response?: { data?: Blob } };
    const data = ax.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const json = JSON.parse(text) as { error?: string };
        throw new Error(json.error || text);
      } catch {
        throw new Error(text || 'PDF download failed');
      }
    }
    throw err;
  }
};

export const getManagementOrder = (
  id: string
): Promise<ManagementOrderDetailResponse> =>
  goRequest<ManagementOrderDetailResponse>({
    url: `${BASE}/orders/${id}`,
    method: 'GET',
  });

export const approveManagementOrder = (
  id: string
): Promise<ManagementOrderApprovalActionResponse> =>
  goRequest<ManagementOrderApprovalActionResponse>({
    url: `${BASE}/orders/${id}/approve`,
    method: 'POST',
  });

export const rejectManagementOrder = (
  id: string
): Promise<ManagementOrderApprovalActionResponse> =>
  goRequest<ManagementOrderApprovalActionResponse>({
    url: `${BASE}/orders/${id}/reject`,
    method: 'POST',
  });

export const cancelManagementOrder = (
  id: string
): Promise<ManagementOrderApprovalActionResponse> =>
  goRequest<ManagementOrderApprovalActionResponse>({
    url: `${BASE}/orders/${id}/cancel`,
    method: 'POST',
  });

export const reopenManagementOrder = (
  id: string
): Promise<ManagementOrderApprovalActionResponse> =>
  goRequest<ManagementOrderApprovalActionResponse>({
    url: `${BASE}/orders/${id}/reopen`,
    method: 'POST',
  });

export const refundManagementOrder = (
  id: string,
  data: ManagementOrderRefundRequest
): Promise<ManagementOrderRefundResponse> =>
  goRequest<ManagementOrderRefundResponse>({
    url: `${BASE}/orders/${id}/refund`,
    method: 'POST',
    data,
  });

export const resolveOrderPaymentMemo = (
  id: string,
  data: ResolveOrderPaymentMemoRequest
): Promise<OrderPaymentMemoDetail> =>
  goRequest<OrderPaymentMemoDetail>({
    url: `${BASE}/orders/${id}/memo/resolve`,
    method: 'POST',
    data,
  });

export const downloadOrderPaymentMemoPdf = async (
  id: string
): Promise<Blob> => {
  try {
    const response = await goClient.get<Blob>(`${BASE}/orders/${id}/memo/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (err: unknown) {
    const ax = err as { response?: { data?: Blob } };
    const data = ax.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const json = JSON.parse(text) as { error?: string };
        throw new Error(json.error || text);
      } catch {
        throw new Error(text || 'PDF download failed');
      }
    }
    throw err;
  }
};

export const updateManagementOrder = (
  id: string,
  data: ManagementUpdateOrderRequest
): Promise<ManagementOrderDetailResponse> =>
  goRequest<ManagementOrderDetailResponse>({
    url: `${BASE}/orders/${id}`,
    method: 'PATCH',
    data,
  });

export const patchManagementOrderLineCustomText = (
  orderId: string,
  lineId: string,
  data: ManagementUpdateOrderLineCustomTextRequest
): Promise<ManagementOrderDetailResponse> =>
  goRequest<ManagementOrderDetailResponse>({
    url: `${BASE}/orders/${orderId}/lines/${lineId}/custom-text`,
    method: 'PATCH',
    data,
  });

export const createOrderShipment = (
  orderId: string,
  data: CreateOrderShipmentRequest
): Promise<OrderShipmentResponse> =>
  goRequest<OrderShipmentResponse>({
    url: `${BASE}/orders/${orderId}/shipments`,
    method: 'POST',
    data,
  });

export const patchOrderShipment = (
  orderId: string,
  shipmentId: string,
  data: UpdateOrderShipmentRequest
): Promise<OrderShipmentResponse> =>
  goRequest<OrderShipmentResponse>({
    url: `${BASE}/orders/${orderId}/shipments/${shipmentId}`,
    method: 'PATCH',
    data,
  });

export const deleteOrderShipment = (
  orderId: string,
  shipmentId: string
): Promise<void> =>
  goRequest<void>({
    url: `${BASE}/orders/${orderId}/shipments/${shipmentId}`,
    method: 'DELETE',
  });

// --- Inventory ---

export const listInventory = (params?: {
  limit?: number;
  offset?: number;
}): Promise<InventoryListResponse> => {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  const q = search.toString();
  return goRequest<InventoryListResponse>({
    url: `${BASE}/inventory${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const adjustInventory = (
  itemId: string,
  data: AdjustInventoryRequest
): Promise<InventoryResponse> =>
  goRequest<InventoryResponse>({
    url: `${BASE}/inventory/${itemId}`,
    method: 'PATCH',
    data,
  });

export const listInventoryTransactionTypes = (): Promise<
  InventoryTransactionTypeResponse[]
> =>
  goRequest<InventoryTransactionTypeResponse[]>({
    url: `${BASE}/inventory/transaction-types`,
    method: 'GET',
  });

export const listInventoryTransactions = (params?: {
  item_id?: string;
  transaction_type_id?: string;
  limit?: number;
  offset?: number;
}): Promise<InventoryTransactionListResponse> => {
  const search = new URLSearchParams();
  if (params?.item_id != null && params.item_id !== '')
    search.set('item_id', params.item_id);
  if (params?.transaction_type_id != null && params.transaction_type_id !== '')
    search.set('transaction_type_id', params.transaction_type_id);
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  const q = search.toString();
  return goRequest<InventoryTransactionListResponse>({
    url: `${BASE}/inventory/transactions${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const kitAssembly = (
  data: KitAssemblyRequest
): Promise<{ message: string }> =>
  goRequest<{ message: string }>({
    url: `${BASE}/inventory/kit-assembly`,
    method: 'POST',
    data,
  });

// --- Online items ---

export const listOnlineItems = (params?: {
  limit?: number;
  offset?: number;
  /** Broad search: online name, tags, linked item name */
  q?: string;
  /** Legacy comma-separated tag terms (used when `q` is empty) */
  tags?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}): Promise<OnlineItemListResponse> => {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  if (params?.q != null && params.q.trim() !== '')
    search.set('q', params.q.trim());
  if (params?.tags != null && params.tags.trim() !== '')
    search.set('tags', params.tags.trim());
  if (params?.sort_by != null && params.sort_by.trim() !== '')
    search.set('sort_by', params.sort_by.trim());
  if (params?.sort_order != null) search.set('sort_order', params.sort_order);
  const q = search.toString();
  return goRequest<OnlineItemListResponse>({
    url: `${BASE}/online-items${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const getOnlineItem = (id: string): Promise<OnlineItemDetailResponse> =>
  goRequest<OnlineItemDetailResponse>({
    url: `${BASE}/online-items/${id}`,
    method: 'GET',
  });

export const createOnlineItem = (
  data: CreateOnlineItemRequest
): Promise<OnlineItemResponse> =>
  goRequest<OnlineItemResponse>({
    url: `${BASE}/online-items`,
    method: 'POST',
    data,
  });

export const updateOnlineItem = (
  id: string,
  data: UpdateOnlineItemRequest
): Promise<OnlineItemResponse> =>
  goRequest<OnlineItemResponse>({
    url: `${BASE}/online-items/${id}`,
    method: 'PATCH',
    data,
  });

export const deleteOnlineItem = (id: string): Promise<{ message: string }> =>
  goRequest<{ message: string }>({
    url: `${BASE}/online-items/${id}`,
    method: 'DELETE',
  });

export const uploadOnlineItemImage = (
  file: File
): Promise<UploadOnlineItemImageResponse> => {
  const form = new FormData();
  form.append('image', file);
  return formGoClient
    .post<UploadOnlineItemImageResponse>(
      `${BASE}/online-items/upload-image`,
      form
    )
    .then((res) => res.data);
};

export const addOnlineItemImage = (
  onlineItemId: string,
  data: AddOnlineItemImageRequest
): Promise<{ message: string }> =>
  goRequest<{ message: string }>({
    url: `${BASE}/online-items/${onlineItemId}/images`,
    method: 'POST',
    data,
  });

export const deleteOnlineItemImage = (
  onlineItemId: string,
  imageId: string
): Promise<{ message: string }> =>
  goRequest<{ message: string }>({
    url: `${BASE}/online-items/${onlineItemId}/images/${imageId}`,
    method: 'DELETE',
  });

// --- Storefront ---

export interface ListStorefrontItemsParams {
  search?: string;
  category_id?: string;
  sub_category_id?: string;
  item_type?: string;
  featured?: boolean;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export const listStorefrontItems = (
  params?: ListStorefrontItemsParams
): Promise<StorefrontItemsResponse> => {
  const search = new URLSearchParams();
  if (params?.search?.trim()) search.set('search', params.search.trim());
  if (params?.category_id?.trim())
    search.set('category_id', params.category_id.trim());
  if (params?.sub_category_id?.trim())
    search.set('sub_category_id', params.sub_category_id.trim());
  if (params?.item_type?.trim())
    search.set('item_type', params.item_type.trim());
  if (params?.featured != null)
    search.set('featured', params.featured ? '1' : '0');
  if (params?.page != null) search.set('page', String(params.page));
  if (params?.page_size != null)
    search.set('page_size', String(params.page_size));
  if (params?.sort_by?.trim()) search.set('sort_by', params.sort_by.trim());
  if (params?.sort_order) search.set('sort_order', params.sort_order);
  const q = search.toString();
  return goRequest<StorefrontItemsResponse>({
    url: `${BASE}/storefront/items${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const getStorefrontItem = (
  id: string
): Promise<OnlineItemDetailResponse> =>
  goRequest<OnlineItemDetailResponse>({
    url: `${BASE}/storefront/items/${id}`,
    method: 'GET',
  });

export const checkoutStorefront = (
  data: StorefrontCheckoutRequest
): Promise<StorefrontCheckoutResponse> =>
  goRequest<StorefrontCheckoutResponse>({
    url: `${BASE}/storefront/checkout`,
    method: 'POST',
    data,
  });

export const confirmStorefrontPayment = (
  data: StorefrontConfirmPaymentRequest
): Promise<StorefrontConfirmPaymentResponse> =>
  goRequest<StorefrontConfirmPaymentResponse>({
    url: `${BASE}/storefront/payments/confirm`,
    method: 'POST',
    data,
  });

export const listStorefrontOrders = (params?: {
  limit?: number;
  offset?: number;
}): Promise<StorefrontOrdersResponse> => {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  const q = search.toString();
  return goRequest<StorefrontOrdersResponse>({
    url: `${BASE}/storefront/orders${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const getStorefrontOrder = (
  id: string
): Promise<StorefrontOrderDetailResponse> =>
  goRequest<StorefrontOrderDetailResponse>({
    url: `${BASE}/storefront/orders/${id}`,
    method: 'GET',
  });
